import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Site } from 'graphql-common';
import { OrderingParams, PaginationParams } from '@lib/enums/urls';
import useSitesQueryHook from 'utils/fetch/hooks/useSitesQueryHook';
import {
  PerformanceWidget,
  defaultPaginationState,
  ORDERING_PARAMS,
} from '../PerformanceWidget';
import getPerformanceWidgetColumns, {
  PerformanceWidgetEntityType,
} from '../PerformanceWidget/utils/getPerformanceWidgetColumns';

export interface SitesPerformanceWidgetProps {
  title?: string;
  showBadge?: boolean;
}

export default function SitesPerformanceWidget({
  title,
  showBadge = false,
}: SitesPerformanceWidgetProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const orderingState = useState<OrderingParams>({ ...ORDERING_PARAMS });
  const paginationState = useState<PaginationParams>(defaultPaginationState);
  const [orderingParams] = orderingState;
  const [paginationParams] = paginationState;

  const {
    collection: sites,
    totalCount,
    loading,
    error,
  } = useSitesQueryHook({
    fetchPolicy: 'cache-and-network',
    paginationParams: {
      ...paginationParams,
      ...orderingParams,
    },
    searchQuery,
    includeFullData: false,
    includePerformanceDataByTaskDeadlinesMissed: true,
  });

  const columns = useMemo(
    () =>
      getPerformanceWidgetColumns({
        t,
        entityType: PerformanceWidgetEntityType.Site,
      }),
    [t],
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <PerformanceWidget
      title={title || t('site-deadline-efficiency-tracker')}
      showBadge={showBadge}
      searchPlaceholder={t('search-by-site-name')}
      data={sites as Site[]}
      totalCount={totalCount}
      loading={loading}
      error={error}
      columns={columns}
      onSearchChange={handleSearchChange}
      paginationState={paginationState}
      orderingState={orderingState}
    />
  );
}
